/*
  Header navigation, search, Shopify Inbox and the mini cart, from sections/header.
  Native links remain the fallback. Cart removals share the checkout queue; the
  panel is redrawn from Shopify after the last change, with a real bag link on failure.
  Notes travel with checkout and save through the same queue for wallet handoffs.
  Added pieces reveal the header and animate its bag without depending on that animation.
*/
// Markets live under a folder (/en-de/): never fetch a fixed root path.
import { ROOT, request, mutate } from './capra-cart.js';

const bar = document.querySelector('[data-bar]');
const sectionId = bar?.closest('.shopify-section')?.id.replace('shopify-section-', '');

if (bar) {
  const searchForm = bar.querySelector('[data-search]');
  const searchOpen = bar.querySelector('[data-search-open]');
  const miniCart = bar.querySelector('[data-mini-cart]');
  const bag = bar.querySelector('[data-bag]');
  let bagMomentUntil = 0;
  let cartFresh = true;
  let refreshRevision = 0;

  /* --- Slide away while scrolling down --- */

  let lastY = scrollY;
  let downFrom = scrollY;
  let ticking = false;
  const searching = () => bar.classList.contains('is-searching');
  const keep = () => scrollY < 4 || Date.now() < bagMomentUntil || searching() ||
    miniCart?.matches(':popover-open') || bar.matches(':hover, :focus-within') || bar.querySelector('.bar__menu[open]');
  // Only the phone menu itself counts: its current section is open by default, so any open details would keep the bar forever.
  const show = (shown) => bar.classList.toggle('is-away', !shown);

  addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      const y = scrollY;
      // Gone after a deliberate move down (not a nudge), back on a deliberate move up.
      if (y <= lastY) downFrom = y;
      if (keep()) show(true);
      else if (y - downFrom > 48) show(false);
      else if (y < lastY - 6) show(true);
      if (y > lastY || Math.abs(y - lastY) > 6) lastY = y;
    });
  }, { passive: true });

  /* --- Search: a bar across the row --- */

  const closeSearch = () => {
    if (!searching()) return;
    bar.classList.remove('is-searching');
    searchForm.hidden = true;
    searchOpen.setAttribute('aria-expanded', 'false');
    searchOpen.focus({ preventScroll: true });
  };
  searchOpen?.addEventListener('click', (event) => {
    if (!searchForm) return;
    event.preventDefault();
    searchForm.hidden = false;
    bar.classList.add('is-searching');
    searchOpen.setAttribute('aria-expanded', 'true');
    searchForm.querySelector('input[type="search"]').focus();
  });
  bar.querySelector('[data-search-close]')?.addEventListener('click', closeSearch);
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeSearch(); });
  document.addEventListener('pointerdown', (event) => {
    if (searching() && !searchForm.contains(event.target) && !searchOpen.contains(event.target)) closeSearch();
  });

  /* --- Menus: Esc closes the open one (content shown on hover or focus can be dismissed, WCAG 1.4.13) --- */

  const phoneMenu = bar.querySelector('.bar__menu');
  // The panel ends at the window's foot wherever the bar sits (a notice may be above it).
  phoneMenu?.addEventListener('toggle', () => {
    if (phoneMenu.open) phoneMenu.style.setProperty('--menu-top', `${bar.getBoundingClientRect().bottom}px`);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (phoneMenu?.open) {
      phoneMenu.open = false;
      phoneMenu.querySelector('summary').focus();
    }
    for (const item of bar.querySelectorAll('.bar__item:is(:hover, :focus-within)')) {
      item.classList.add('is-dismissed');
      const reset = () => item.classList.remove('is-dismissed');
      item.addEventListener('pointerleave', reset, { once: true });
      item.addEventListener('focusout', reset, { once: true });
    }
  });

  /* --- Chat: the bubble opens Shopify Inbox; without it, the link goes to the contact page --- */

  const chatButton = bar.querySelector('[data-chat]');
  const inbox = () => (customElements.get('shopify-chat') ? document.querySelector('shopify-chat') : null);

  // Staff hours, in the store's time zone: someone answers the chat only then (sections/header.liquid).
  const WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const staffed = () => {
    try {
      const hours = JSON.parse(chatButton.dataset.chatHours);
      const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
        timeZone: chatButton.dataset.chatZone, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      }).formatToParts(new Date()).map((part) => [part.type, part.value]));
      const day = hours[WEEKDAYS[parts.weekday]];
      const now = `${parts.hour}:${parts.minute}`;
      return Boolean(day) && now >= day[0] && now < day[1];
    } catch {
      return false; // unknown hours: the contact form always reaches someone
    }
  };
  // Named for where it leads: the chat in staff hours, the contact form outside them.
  const name = () => {
    const label = chatButton?.querySelector('[data-chat-name]');
    if (label && inbox() && staffed()) label.textContent = chatButton.dataset.chatLabel;
  };

  chatButton?.addEventListener('click', (event) => {
    const chat = inbox();
    if (!chat) return;
    // Outside staff hours the link goes to the contact form, unless a reply is waiting or the chat is open.
    if (!staffed() && !chat.open && !chatButton.classList.contains('has-unread')) return;
    event.preventDefault();
    if (chat.open) chat.close();
    else chat.show();
  });
  // The chat says whether it is open with its own attribute: the bubble follows it.
  customElements.whenDefined('shopify-chat').then(() => {
    const chat = inbox();
    if (!chat || !chatButton) return;
    name();
    const sync =() => chatButton.setAttribute('aria-expanded', String(chat.hasAttribute('open')));
    sync();
    new MutationObserver(sync).observe(chat, { attributes: true, attributeFilter: ['open'] });

    // Its unread dot sits on its own (hidden) button, in its shadow DOM; the runtime that draws it
    // loads later in a shadow root of its own, so each root found is watched as it appears.
    // If Inbox changes its markup the dot is simply never shown.
    const unread = chatButton.querySelector('[data-chat-unread]');
    const watched = new WeakSet();
    const look = () => {
      let root = chat.shadowRoot;
      let active = false;
      while (root) {
        if (!watched.has(root)) {
          watched.add(root);
          new MutationObserver(look).observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
        }
        active ||= Boolean(root.querySelector('.activator__indicator--active'));
        root = root.querySelector('shopify-agent-runtime')?.shadowRoot;
      }
      chatButton.classList.toggle('has-unread', active);
      if (unread) unread.hidden = !active;
    };
    look();
  });

  /* --- The mini cart --- */

  const body = () => miniCart.querySelector('[data-mini-cart-body]');

  // Light dismiss closes it before the click lands on the bag: remember it was open.
  let wasOpen = false;
  bag?.addEventListener('pointerdown', () => { wasOpen = Boolean(miniCart?.matches(':popover-open')); });
  // A second press on the bag closes the panel (owner, 2026-10-22), on a phone and on a desktop; on the
  // bag page itself, where the panel would only repeat the page, it is the link (owner, 2026-10-19).
  bag?.addEventListener('click', (event) => {
    if (!miniCart) return;
    if (wasOpen || miniCart.matches(':popover-open')) {
      wasOpen = false;
      event.preventDefault();
      if (miniCart.matches(':popover-open')) miniCart.hidePopover();
      return;
    }
    if (new URL(bag.href).pathname === location.pathname) return;
    if (!cartFresh) return; // the real bag link recovers a failed section refresh
    event.preventDefault();
    show(true);
    miniCart.style.setProperty('--minicart-top', `${bar.getBoundingClientRect().bottom}px`);
    miniCart.showPopover();
    miniCart.focus({ preventScroll: true });
  });
  bag?.addEventListener('pointercancel', () => { wasOpen = false; });
  miniCart?.addEventListener('toggle', (event) => {
    bag.setAttribute('aria-expanded', String(event.newState === 'open'));
    if (event.newState === 'closed' && miniCart.contains(document.activeElement)) bag.focus({ preventScroll: true });
  });

  const counts = (count) => {
    for (const el of document.querySelectorAll('[data-cart-count]')) {
      el.textContent = count;
      el.hidden = count === 0;
    }
  };

  // Put fresh server HTML in place: the mini cart body and the bag's fill.
  const apply = (html) => {
    const fresh = new DOMParser().parseFromString(html, 'text/html');
    const next = fresh.querySelector('[data-mini-cart-body]');
    if (!next) throw new Error('cart section');
    body().replaceWith(next);
    const count = fresh.querySelector('[data-cart-count]');
    if (count) counts(Number(count.textContent) || 0);
    return fresh.querySelector('[data-bar]')?.classList.contains('has-items');
  };

  const refresh = async () => {
    const revision = ++refreshRevision;
    cartFresh = false;
    const html = await request(`${location.pathname}?section_id=${sectionId}`, {}, 'text');
    if (revision !== refreshRevision) return;
    const full = apply(html);
    cartFresh = true;
    return full;
  };

  // Requests share the checkout queue. Hidden rows are restored on failure, with a native bag link.
  let waiting = 0;
  const take = async (key, quantity) => {
    cartFresh = false;
    refreshRevision++;
    try {
      const cart = await mutate('change', {
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: sectionId }),
      });
      if (--waiting) return;
      apply(cart.sections?.[sectionId]);
      cartFresh = true;
      bar.classList.toggle('has-items', cart.item_count > 0);
      counts(cart.item_count);
      miniCart.focus({ preventScroll: true });
    } catch {
      waiting = Math.max(0, waiting - 1);
      for (const row of miniCart.querySelectorAll('.is-leaving')) {
        row.classList.remove('is-leaving');
        row.querySelector('[data-remove]')?.removeAttribute('aria-disabled');
      }
      miniCart.querySelector('[data-cart-error]').hidden = false;
    }
  };

  miniCart?.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-remove]');
    if (!remove) return;
    event.preventDefault();
    if (remove.getAttribute('aria-disabled') === 'true') return;
    remove.setAttribute('aria-disabled', 'true');
    const row = remove.closest('li');
    row.style.maxHeight = `${row.offsetHeight}px`;
    void row.offsetHeight;
    row.classList.add('is-leaving');
    // One row per piece: the piece's rows still in place are how many of it stay
    const key = remove.dataset.remove;
    const quantity = [...miniCart.querySelectorAll('[data-remove]')]
      .filter((cross) => cross.dataset.remove === key && !cross.closest('li').classList.contains('is-leaving')).length;
    waiting += 1;
    void take(key, quantity);
  });

  // The bag page changed the cart (capra-bag.js): the panel and the bag's fill follow
  document.addEventListener('capra:cart-changed', (event) => {
    refresh().catch(() => {});
    const count = event.detail?.count ?? 0;
    bar.classList.toggle('has-items', count > 0);
    counts(count);
  });

  /* --- The cart's note (snippets/cart-note): saved as it is typed, the same in every field --- */

  // Opening "Add a note" puts the cursor in the field. A phone raises its keyboard only for a focus made
  // inside the tap itself, not for one made when the details' toggle event arrives a moment later, so the
  // tap opens it by hand (the details' own toggle would otherwise close it again).
  document.addEventListener('click', (event) => {
    const label = event.target.closest?.('.cart-note__label');
    const note = label?.parentElement;
    if (!note || note.open) return;
    event.preventDefault();
    note.open = true;
    const field = note.querySelector('[data-cart-note]');
    field?.focus();
    field?.scrollIntoView({ block: 'nearest' });
  });
  document.addEventListener('toggle', (event) => {
    if (event.target.matches?.('.cart-note') && event.target.open) event.target.querySelector('[data-cart-note]')?.focus();
  }, true);

  let noteValue, noteQueued = false, noteTimer, flushNote;
  document.addEventListener('capra:checkout', () => flushNote?.());
  document.addEventListener('input', (event) => {
    const field = event.target.closest('[data-cart-note]');
    if (!field) return;
    noteValue = field.value;
    for (const other of document.querySelectorAll('[data-cart-note]')) if (other !== field) other.value = field.value;
    clearTimeout(noteTimer);
    if (flushNote) noteTimer = setTimeout(flushNote, 500);
    if (noteQueued) return;
    noteQueued = true;
    // Reserve the write now so checkout waits, but send only the latest note after typing pauses.
    mutate('update', () => new Promise((resolve) => {
      flushNote = () => {
        clearTimeout(noteTimer);
        noteQueued = false;
        flushNote = null;
        resolve({
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: noteValue }),
        });
      };
      noteTimer = setTimeout(flushNote, 500);
    })).catch(() => { noteQueued = false; miniCart.querySelector('[data-cart-error]').hidden = false; });
  });

  /* --- After an add --- */

  document.addEventListener('capra:added', async (event) => {
    const handoff = event.detail?.handoff ?? 0;
    bagMomentUntil = Date.now() + handoff + 1600;
    show(true);
    const empty = !bar.classList.contains('has-items');
    if (empty) bar.classList.add('is-bag-waiting'); // an empty bag stays empty until the writing starts
    cartFresh = false;
    refresh().catch(() => { miniCart.querySelector('[data-cart-error]').hidden = false; });
    setTimeout(() => {
      bar.classList.remove('is-bag-waiting', 'is-receiving');
      bar.classList.add('has-items');
      void bar.offsetWidth;
      bar.classList.add('is-receiving');
      setTimeout(() => bar.classList.remove('is-receiving'), 1500);
    }, handoff);
  });
}
